import api from './api'
import type { LoginCredentials, ResetPasswordData, LoginResponse, UserAuthData, TwoFactorVerifyData } from '@/types/auth'

/**
 * Unwrap the `{ success, message, data }` envelope. A 2xx body without a
 * `data` field used to resolve as `undefined` cast to the return type, so
 * callers blew up later on `.posToken` instead of failing here.
 */
function requireData<T>(response: { data?: unknown }, context: string): T {
    const payload = (response.data as { data?: T } | undefined)?.data
    if (payload === undefined || payload === null) {
        throw new Error(`Unexpected ${context} response shape: missing 'data'`)
    }
    return payload
}

export default {
    async login(credentials: LoginCredentials): Promise<LoginResponse> {
        const response = await api.post('/getAccessToken', credentials)
        // Backend wraps every response as { success, message, data: {...} }
        // (see BaseController::sendResponse). Unwrap the payload so callers
        // receive the flat LoginResponse (posToken / tenantId / expiresIn).
        return requireData<LoginResponse>(response, 'login')
    },

    /**
     * Complete the 2FA challenge for Passport token clients. The provisional
     * 2FA_PENDING token (held by the Bearer header) is exchanged for a full
     * "POS Token" on success.
     */
    async verifyTwoFactor(data: TwoFactorVerifyData): Promise<LoginResponse> {
        const response = await api.post('/two-factor/verify', data)
        const resData = (response.data?.data || response.data || {}) as Record<string, any>
        const posToken = resData.posToken ?? resData.POS_token ?? resData.token
        const expiresIn = resData.expiresIn ?? resData.expires_in
        const result: Record<string, any> = { ...resData }
        if (posToken !== undefined) {
            result.posToken = posToken
        }
        if (expiresIn !== undefined) {
            result.expiresIn = expiresIn
        }
        return result as LoginResponse
    },

    async logout(): Promise<void> {
        await api.post('/logout')
    },

    async forgotPassword(email: string): Promise<unknown> {
        const response = await api.post('/password/create', { email })
        return response.data
    },

    async resetPassword(data: ResetPasswordData): Promise<unknown> {
        const response = await api.post('/password/reset', {
            token: data.token,
            email: data.email,
            password: data.password,
            passwordConfirmation: data.passwordConfirmation
        })
        return response.data
    },

    async me(): Promise<unknown> {
        const response = await api.get('/me')
        return response.data
    },

    async getUserAuth(): Promise<UserAuthData> {
        const response = await api.get('/get_user_auth')
        return requireData<UserAuthData>(response, 'user auth')
    },
}
